import { useCallback, useEffect, useState } from 'react'
import { Box, Button, CloseButton, Dialog, Flex, Portal, Skeleton, Text } from '@chakra-ui/react'
import { Link as RouterLink, Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'
import { LuChartColumn, LuEye, LuFilm, LuLayoutDashboard, LuLayoutGrid, LuTrophy } from 'react-icons/lu'
import { useAuth } from '../contexts/AuthContext'
import { EventWorkspaceProvider, sectionStateFor, useEventWorkspace } from '../contexts/EventWorkspaceContext'
import { EventSidebar } from '../components/eventWorkspace/EventSidebar'
import { findSection, visibleGroups } from '../components/eventSettings/sections'
import { toaster } from '../components/ui/toaster'
import { useTranslation } from '../i18n'

// The event's pages, in sidebar order. `key` is also how the current page is
// recognised from the URL (see currentKey).
function runItemsFor(ws, base) {
  const items = [
    { key: 'overview', labelKey: 'workspace.overview', icon: LuLayoutDashboard, to: base },
    // Moderation keeps its own full-screen route outside the app chrome.
    { key: 'moderation', labelKey: 'events.moderation', icon: LuEye, to: `${base}/moderation`, external: true },
  ]
  if (ws.canSeeVideos) {
    items.push({ key: 'videos', labelKey: 'events.videos', icon: LuLayoutGrid, to: `${base}/videos` })
    items.push({ key: 'reels', labelKey: 'events.reels', icon: LuFilm, to: `${base}/reels` })
  }
  if (ws.canPrizes && ws.event?.challengeMode && ws.isEventAdmin) {
    items.push({ key: 'prizes', labelKey: 'events.prizes', icon: LuTrophy, to: `${base}/prizes` })
  }
  if (ws.isEventAdmin) {
    items.push({ key: 'analytics', labelKey: 'analytics.navLink', icon: LuChartColumn, to: `${base}/analytics` })
  }
  return items
}

function currentKey(pathname, base) {
  const rest = pathname.slice(base.length).replace(/^\/|\/$/g, '')
  if (rest === '') return 'overview'
  const [first, second] = rest.split('/')
  return first === 'settings' ? second : first
}

function Workspace() {
  const ws = useEventWorkspace()
  const { t, lang } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const base = `/events/${ws.eventId}`
  const current = currentKey(location.pathname, base)
  const [pending, setPending] = useState(null)
  const [savingToLeave, setSavingToLeave] = useState(false)

  const s = sectionStateFor(ws, null)
  const runItems = runItemsFor(ws, base)
  const settingsGroups = visibleGroups(s).map((g) => ({
    ...g,
    items: g.sections.map((sec) => ({ key: sec.id, labelKey: sec.labelKey, icon: sec.icon, to: `${base}/settings/${sec.id}` })),
  }))

  // A tab left without changes simply stops being edited — whatever moved us
  // (the sidebar, Back, a header link). With changes, the draft is kept and the
  // tab is marked, so nothing is lost to a navigation the sidebar never saw.
  useEffect(() => {
    if (ws.editingSection && ws.editingSection !== current && !ws.unsaved) ws.discardEdit()
  }, [current, ws])

  // Reloading or closing the tab is the one way out that loses the draft, so
  // the browser asks first — as the old single event page did.
  useEffect(() => {
    if (!ws.unsaved) return
    const handler = (e) => { e.preventDefault() }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [ws.unsaved])

  const go = useCallback((item) => {
    if (!item) return
    navigate(item.to)
  }, [navigate])

  const leavingUnsaved = (item) =>
    ws.unsaved && item.key !== ws.editingSection

  const onNavigate = (e, item) => {
    if (!leavingUnsaved(item)) return
    e.preventDefault()
    setPending(item)
  }

  const onSelect = (item) => {
    if (!item) return
    if (leavingUnsaved(item)) setPending(item)
    else go(item)
  }

  const editingLabel = ws.editingSection ? t(findSection(ws.editingSection)?.labelKey ?? '') : ''

  const leaveWith = async (action) => {
    const target = pending
    if (action === 'save') {
      setSavingToLeave(true)
      try {
        await ws.saveEdit()
        toaster.create({ title: t('events.eventUpdated'), status: 'success' })
      } catch (err) {
        toaster.create({ title: t('events.eventUpdateFailed'), description: err.message, status: 'error' })
        setSavingToLeave(false)
        return
      }
      setSavingToLeave(false)
    } else {
      ws.discardEdit()
    }
    setPending(null)
    go(target)
  }

  if (ws.loading) {
    return (
      <Flex gap={8}>
        <Skeleton w="240px" h="420px" display={{ base: 'none', lg: 'block' }} />
        <Skeleton flex="1" h="420px" />
      </Flex>
    )
  }
  if (ws.error || !ws.event) {
    return <Text color="fg.muted">{t('workspace.notFound')}</Text>
  }

  const typeLabel = ws.typeConfig?.label?.[lang] || ws.typeConfig?.label?.en
  const showUnsavedBanner = ws.unsaved && current !== ws.editingSection

  return (
    <Flex direction={{ base: 'column', lg: 'row' }} gap={{ base: 4, lg: 10 }} align={{ base: 'stretch', lg: 'flex-start' }}>
      <EventSidebar
        event={ws.event}
        eventId={ws.eventId}
        typeLabel={typeLabel}
        runItems={runItems}
        settingsGroups={settingsGroups}
        current={current}
        unsavedKey={ws.unsaved ? ws.editingSection : null}
        onNavigate={onNavigate}
        onSelect={onSelect}
      />

      <Box as="main" flex="1" minW={0}>
        {showUnsavedBanner && (
          <Flex
            role="status"
            align="center"
            gap={3}
            wrap="wrap"
            mb={6}
            px={4}
            py={3}
            borderRadius="md"
            bg="var(--mc-amber-bg)"
            color="var(--mc-text)"
          >
            <Text flex="1" fontSize="sm">{t('workspace.unsavedElsewhere', { tab: editingLabel })}</Text>
            <Button asChild size="xs" variant="outline">
              <RouterLink to={`${base}/settings/${ws.editingSection}`}>{t('workspace.backToTab')}</RouterLink>
            </Button>
          </Flex>
        )}
        <Outlet />
      </Box>

      <Dialog.Root open={!!pending} onOpenChange={(e) => { if (!e.open && !savingToLeave) setPending(null) }} role="alertdialog">
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>
                <Dialog.Title>{t('workspace.leaveTitle', { tab: editingLabel })}</Dialog.Title>
              </Dialog.Header>
              <Dialog.Body>
                <Text>{t('workspace.leaveBody')}</Text>
              </Dialog.Body>
              <Dialog.Footer flexWrap="wrap">
                <Button variant="outline" disabled={savingToLeave} onClick={() => setPending(null)}>{t('workspace.stay')}</Button>
                <Button variant="outline" colorPalette="red" disabled={savingToLeave} onClick={() => leaveWith('discard')}>{t('workspace.discard')}</Button>
                <Button colorPalette="brand" loading={savingToLeave} onClick={() => leaveWith('save')}>{t('workspace.saveAndLeave')}</Button>
              </Dialog.Footer>
              <Dialog.CloseTrigger asChild>
                <CloseButton size="sm" disabled={savingToLeave} />
              </Dialog.CloseTrigger>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </Flex>
  )
}

/**
 * `/events/:eventId/*` — one event's workspace: its pages and its settings in
 * one sidebar, the page itself beside it. Moderators never get here: their
 * only view of an event is the moderation screen, so a pasted URL sends them
 * there.
 */
export default function EventWorkspace() {
  const { eventId } = useParams()
  const { isModeratorOnly } = useAuth()
  if (isModeratorOnly(eventId)) return <Navigate to={`/events/${eventId}/moderation`} replace />
  // Keyed by event, so switching events never carries a draft across.
  return (
    <EventWorkspaceProvider key={eventId} eventId={eventId}>
      <Workspace />
    </EventWorkspaceProvider>
  )
}
